import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import { useAuthStore, useCurrentUser } from "@/stores/auth";
import { useBrand } from "@/stores/instance";
import { useUnreadCount } from "@/features/notifications/useUnreadCount";
import {
  IconHome,
  IconCompass,
  IconPaperclip,
  IconInbox,
  IconSettings,
  IconInfo,
  IconSignIn,
  IconSignOut,
  IconArchive,
  IconFilter,
  IconMenu,
  IconX,
  IconPlus,
  IconArticle,
} from "@/components/icons";
import { UserAvatar } from "@/components/UserAvatar";

interface NavItem {
  to: string;
  labelKey: string;
  icon: ReactNode;
  badge?: number;
}

export function AppLayout({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const user = useCurrentUser();
  const brand = useBrand();
  const signOut = useAuthStore((s) => s.signOut);
  const unread = useUnreadCount();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isAuthenticated = Boolean(user);

  const authItems: NavItem[] = [
    {
      to: "/",
      labelKey: "nav.home",
      icon: <IconHome width={20} height={20} />,
    },
    {
      to: "/explore",
      labelKey: "nav.explore",
      icon: <IconCompass width={20} height={20} />,
    },
    {
      to: "/attachments",
      labelKey: "nav.attachments",
      icon: <IconPaperclip width={20} height={20} />,
    },
    {
      to: "/chat-import",
      labelKey: "nav.chatImport",
      icon: <IconArticle width={20} height={20} />,
    },
    {
      to: "/inbox",
      labelKey: "nav.inbox",
      icon: <IconInbox width={20} height={20} />,
      badge: unread,
    },
  ];
  const authSecondary: NavItem[] = [
    {
      to: "/archived",
      labelKey: "nav.archived",
      icon: <IconArchive width={20} height={20} />,
    },
    {
      to: "/shortcuts",
      labelKey: "nav.shortcuts",
      icon: <IconFilter width={20} height={20} />,
    },
    {
      to: "/setting",
      labelKey: "nav.settings",
      icon: <IconSettings width={20} height={20} />,
    },
    {
      to: "/about",
      labelKey: "nav.about",
      icon: <IconInfo width={20} height={20} />,
    },
  ];
  const anonItems: NavItem[] = [
    {
      to: "/explore",
      labelKey: "nav.explore",
      icon: <IconCompass width={20} height={20} />,
    },
    {
      to: "/about",
      labelKey: "nav.about",
      icon: <IconInfo width={20} height={20} />,
    },
  ];

  const primary = isAuthenticated ? authItems : anonItems;

  /** Focus the home composer, navigating there first if needed. */
  function focusComposer() {
    void navigate({ to: "/" });
    // Wait for the editor to be on screen, then focus its textarea.
    window.setTimeout(() => {
      const ta = document.querySelector<HTMLTextAreaElement>("main textarea");
      if (ta) {
        ta.scrollIntoView({ block: "start", behavior: "smooth" });
        ta.focus({ preventScroll: true });
      }
    }, 60);
  }

  const navLink = (item: NavItem, onNavigate?: () => void) => {
    const active =
      item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={onNavigate}
        aria-current={active ? "page" : undefined}
        className={cn(
          "group flex items-center gap-3.5 rounded-full px-4 py-2.5 text-[15px] transition-colors duration-150",
          active
            ? "bg-accent-soft font-semibold text-accent"
            : "font-medium text-fg-soft hover:bg-bg-mute hover:text-fg",
        )}
      >
        <span
          className={cn(
            "relative transition-transform duration-150",
            !active && "group-hover:-translate-y-px",
          )}
        >
          {item.icon}
          {item.badge !== undefined && item.badge > 0 && (
            <span
              aria-label={
                t("nav.inbox") + ` (${item.badge > 99 ? "99+" : item.badge})`
              }
              className="absolute -end-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-danger-fg"
            >
              {item.badge > 99 ? "99+" : item.badge}
            </span>
          )}
        </span>
        <span>{t(item.labelKey)}</span>
      </Link>
    );
  };

  const brandMark = brand.logoUrl ? (
    <img
      src={brand.logoUrl}
      alt=""
      className="h-9 w-9 rounded-xl object-cover shadow-sm"
    />
  ) : (
    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent text-base font-bold text-accent-fg shadow-sm">
      {brand.title.charAt(0).toUpperCase()}
    </div>
  );

  const sidebarContent = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-4 pb-2 pt-5">
        {brandMark}
        <span className="truncate text-lg font-bold tracking-tight text-fg">
          {brand.title}
        </span>
      </div>

      <nav aria-label="Primary" className="mt-3 flex flex-col gap-1 px-3">
        {primary.map((i) => navLink(i))}
      </nav>

      {isAuthenticated && (
        <>
          <nav
            aria-label="Secondary"
            className="mt-3 flex flex-col gap-1 border-t border-border px-3 pt-3"
          >
            {authSecondary.map((i) => navLink(i))}
          </nav>

          {/* Primary composer CTA — recording is the product's main action. */}
          <div className="px-3 pt-4">
            <button
              type="button"
              onClick={focusComposer}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-accent text-[15px] font-semibold text-accent-fg shadow-sm transition-[filter,box-shadow] duration-150 hover:shadow-md hover:brightness-[1.06] active:scale-[0.98]"
            >
              <IconPlus width={18} height={18} />
              {t("nav.compose")}
            </button>
          </div>
        </>
      )}

      <div className="mt-auto p-3">
        {isAuthenticated && user ? (
          <div className="flex items-center gap-2 rounded-2xl border border-border bg-bg-soft p-2 shadow-card">
            <Link
              to="/u/$username"
              params={{ username: user.username }}
              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-xl p-1 transition-colors hover:bg-bg-mute"
            >
              <UserAvatar user={user} size="sm" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-fg">
                  {user.displayName || user.username}
                </span>
                <span className="block truncate text-xs text-fg-mute">
                  @{user.username}
                </span>
              </span>
            </Link>
            <button
              type="button"
              onClick={() => void signOut()}
              aria-label={t("nav.signout")}
              title={t("nav.signout")}
              className="rounded-full p-2 text-fg-mute transition-colors hover:bg-bg-mute hover:text-fg"
            >
              <IconSignOut width={18} height={18} />
            </button>
          </div>
        ) : (
          <Link
            to="/auth"
            className="flex h-11 items-center justify-center gap-2 rounded-full bg-accent text-[15px] font-semibold text-accent-fg shadow-sm transition-[filter,box-shadow] hover:shadow-md hover:brightness-[1.06] active:scale-[0.98]"
          >
            <IconSignIn width={18} height={18} />
            {t("nav.signin")}
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-bg">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-e border-border md:block">
        {sidebarContent}
      </aside>

      {/* Mobile top bar */}
      <div className="fixed inset-x-0 top-0 z-30 flex items-center gap-2 border-b border-border bg-bg/80 px-3 py-2 backdrop-blur-md md:hidden">
        <button
          type="button"
          aria-label="Open navigation"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}
          className="rounded-full p-2 text-fg-soft transition-colors hover:bg-bg-mute"
        >
          <IconMenu />
        </button>
        <span className="flex items-center gap-2">
          {brandMark}
          <span className="truncate text-base font-bold tracking-tight">
            {brand.title}
          </span>
        </span>
        <span className="ms-auto" />
        {isAuthenticated && user && (
          <Link
            to="/inbox"
            aria-label={t("nav.inbox")}
            className="relative rounded-full p-2 text-fg-soft transition-colors hover:bg-bg-mute"
          >
            <IconInbox />
            {unread > 0 && (
              <span className="absolute end-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-danger-fg">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </Link>
        )}
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
        >
          <div
            className="absolute inset-0 bg-black/45 backdrop-blur-[2px] [animation:fade-in_140ms_ease-out]"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-y-0 start-0 w-72 border-e border-border bg-bg shadow-pop [animation:dialog-in_200ms_cubic-bezier(0.22,1,0.36,1)]">
            <div className="flex justify-end p-2">
              <button
                type="button"
                aria-label={t("action.close")}
                onClick={() => setDrawerOpen(false)}
                className="rounded-full p-2 text-fg-mute transition-colors hover:bg-bg-mute"
              >
                <IconX />
              </button>
            </div>
            <div
              onClick={() => setDrawerOpen(false)}
              className="h-[calc(100%-3rem)]"
            >
              {sidebarContent}
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="min-w-0 flex-1 px-4 pb-16 pt-16 sm:px-6 md:pt-6">
        <div
          className={cn(
            "mx-auto w-full",
            pathname.startsWith("/chat-import")
              ? "max-w-6xl"
              : pathname.startsWith("/memos/")
                ? "max-w-3xl"
                : "max-w-2xl",
          )}
        >
          {children}
        </div>
      </main>
    </div>
  );
}
